import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { XCircle, ArrowLeft, RefreshCw, Home, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const submissionId = searchParams.get("submission_id");

  const handleRetry = () => {
    if (submissionId) {
      navigate(`/dba-check/resultaat/${submissionId}`);
    } else {
      navigate("/dba-check");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
      {/* Background gradient effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 text-center max-w-md">
        {/* Cancel Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-24 h-24 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-8"
          data-testid="cancel-icon"
        >
          <XCircle className="w-12 h-12 text-amber-400" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-outfit text-3xl md:text-4xl font-medium text-white mb-4"
          data-testid="cancel-title"
        >
          Betaling geannuleerd
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-zinc-400 text-lg mb-8 leading-relaxed"
          data-testid="cancel-description"
        >
          Geen zorgen, er is niets in rekening gebracht. 
          Je kunt het op elk moment opnieuw proberen.
        </motion.p>

        {/* Info box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-start gap-3 text-left">
            <HelpCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-zinc-300 text-sm mb-2">
                <strong className="text-white">Problemen met betalen?</strong>
              </p>
              <p className="text-zinc-400 text-sm">
                Probeer een andere betaalmethode of neem contact met ons op als het probleem aanhoudt.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            onClick={handleRetry}
            className="rounded-full px-8 py-6 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white font-semibold text-lg shadow-lg shadow-blue-500/25"
            data-testid="retry-payment-btn"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Probeer opnieuw
          </Button>
        </motion.div>

        {/* Secondary actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-6 mt-6"
        >
          {submissionId && (
            <Link 
              to={`/dba-check/resultaat/${submissionId}`}
              className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
              data-testid="back-to-result-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar resultaat
            </Link>
          )}
          <Link 
            to="/"
            className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm"
            data-testid="home-link"
          >
            <Home className="w-4 h-4" />
            Naar homepage
          </Link>
        </motion.div>

        {/* Trust message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-zinc-500 text-sm mt-10"
        >
          Je gratis resultaat blijft beschikbaar
        </motion.p>
      </div>
    </div>
  );
};

export default PaymentCancel;
